import { useCallback, useSyncExternalStore } from 'react'
import { toScheduleEventId } from './diffScheduleEvents'
import {
  type FingerprintableEvent,
  fingerprintScheduleEvent,
} from './fingerprintScheduleEvent'

type Listener = () => void

const DEFAULT_INSTANCE_KEY = ''

const eventsByInstance = new Map<string, Map<number, unknown>>()
const listenersByInstance = new Map<string, Map<number, Set<Listener>>>()

const getEventMap = (instanceKey: string): Map<number, unknown> => {
  let events = eventsByInstance.get(instanceKey)
  if (!events) {
    events = new Map()
    eventsByInstance.set(instanceKey, events)
  }
  return events
}

const getListenerMap = (instanceKey: string): Map<number, Set<Listener>> => {
  let listeners = listenersByInstance.get(instanceKey)
  if (!listeners) {
    listeners = new Map()
    listenersByInstance.set(instanceKey, listeners)
  }
  return listeners
}

const notify = (instanceKey: string, id: number): void => {
  const set = getListenerMap(instanceKey).get(id)
  if (!set) return
  for (const listener of set) listener()
}

const toStoreId = (id: number | string): number => toScheduleEventId(id)

const hasVisibleScheduleChange = (
  previous: unknown,
  next: unknown,
  instanceKey: string,
): boolean => {
  if (previous === next) return false
  if (previous == null || next == null) return true
  if (typeof previous !== 'object' || typeof next !== 'object') return true

  return (
    fingerprintScheduleEvent(previous as FingerprintableEvent, instanceKey) !==
    fingerprintScheduleEvent(next as FingerprintableEvent, instanceKey)
  )
}

export const scheduleEventStore = {
  get: (id: number | string, instanceKey = DEFAULT_INSTANCE_KEY): unknown =>
    getEventMap(instanceKey).get(toStoreId(id)),
  set: (id: number | string, data: unknown, instanceKey = DEFAULT_INSTANCE_KEY): void => {
    const storeId = toStoreId(id)
    const events = getEventMap(instanceKey)
    const previous = events.get(storeId)
    events.set(storeId, data)
    if (hasVisibleScheduleChange(previous, data, instanceKey)) notify(instanceKey, storeId)
  },
  remove: (id: number | string, instanceKey = DEFAULT_INSTANCE_KEY): void => {
    const storeId = toStoreId(id)
    if (getEventMap(instanceKey).delete(storeId)) notify(instanceKey, storeId)
  },
  reset: (instanceKey?: string): void => {
    if (instanceKey != null) {
      const previousIds = [...getEventMap(instanceKey).keys()]
      eventsByInstance.delete(instanceKey)
      for (const id of previousIds) notify(instanceKey, id)
      return
    }

    const instanceKeys = [...eventsByInstance.keys()]
    eventsByInstance.clear()
    for (const key of instanceKeys) {
      const listenerMap = listenersByInstance.get(key)
      if (!listenerMap) continue
      for (const id of listenerMap.keys()) notify(key, id)
    }
  },
  hydrate: <T extends { id: number | string }>(
    items: T[],
    instanceKey = DEFAULT_INSTANCE_KEY,
  ): void => {
    const events = getEventMap(instanceKey)
    const previousById = new Map(events)
    const nextIds = new Set<number>()
    events.clear()
    const changedIds: number[] = []

    for (const item of items) {
      const storeId = toStoreId(item.id)
      nextIds.add(storeId)
      const previous = previousById.get(storeId)
      if (previous && !hasVisibleScheduleChange(previous, item, instanceKey)) {
        events.set(storeId, previous)
        continue
      }
      events.set(storeId, item)
      changedIds.push(storeId)
    }

    for (const id of previousById.keys()) {
      if (!nextIds.has(id)) {
        changedIds.push(id)
      }
    }

    for (const id of new Set(changedIds)) notify(instanceKey, id)
  },
  subscribe: (
    id: number | string,
    listener: Listener,
    instanceKey = DEFAULT_INSTANCE_KEY,
  ): (() => void) => {
    const storeId = toStoreId(id)
    const listeners = getListenerMap(instanceKey)
    let set = listeners.get(storeId)
    if (!set) {
      set = new Set()
      listeners.set(storeId, set)
    }
    set.add(listener)
    return () => {
      set.delete(listener)
      if (set.size === 0) listeners.delete(storeId)
    }
  },
}

export function useScheduleEvent<T>(
  id: number | string | undefined,
  fallback: T,
  instanceKey = DEFAULT_INSTANCE_KEY,
): T {
  const eventId = id == null || id === '' ? undefined : toStoreId(id)
  const subscribe = useCallback(
    (listener: Listener) =>
      eventId == null || Number.isNaN(eventId)
        ? () => undefined
        : scheduleEventStore.subscribe(eventId, listener, instanceKey),
    [eventId, instanceKey],
  )
  const getSnapshot = useCallback(
    () =>
      eventId == null || Number.isNaN(eventId)
        ? undefined
        : scheduleEventStore.get(eventId, instanceKey),
    [eventId, instanceKey],
  )
  const stored = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return (stored as T) ?? fallback
}
